import { hasRepPermissions } from './permissions';

export const PROGRESS_LOG_SUBSCRIPTION_STATUSES = ['ACTIVE', 'PAUSED', 'PHARMACY_CHANGED'];
export const PROGRESS_LOG_PROGRAM_SLUGS = new Set(['wl', 'gmd']);
// the SKU fallback exists because an enrolled subscription does not always carry a programId that
// resolves to one of those programs. The two lists have to admit the same populations: a SKU set
// narrower than the slug set denies a patient whose programs have not loaded yet.
export const PROGRESS_LOG_PRODUCT_SKUS = new Set([
	'SEMAGLUTIDE-G',
	'TIRZEPATIDE-G',
	'OZEMPIC',
	'ZEPBOUND',
	'SEMAGLUTIDE-G-MICRODOSING',
	'TIRZEPATIDE-G-MICRODOSING',
]);

/**
 * Decides whether the signed-in user may reach the progress log, and explains why.
 *
 * @param {object} [options]
 * @param {object} [options.authContext={}] - Current authorization context used for permission checks.
 * @param {Array<object>} [options.subscriptions=[]] - User subscriptions used to detect progress log program enrollment.
 * @param {Array<object>} [options.programs=[]] - Program records used to resolve a subscription's program slug.
 * @returns {object} Access decision with the per-subscription reasoning behind it.
 */
export const getProgressLogAccessDecision = ({ authContext = {}, subscriptions = [], programs = [] } = {}) => {
	const subscriptionSummaries = subscriptions.map((subscription) => {
		const enrolledProgram = programs.find((program) => program?.id === subscription?.programId);
		const isActiveStatus = PROGRESS_LOG_SUBSCRIPTION_STATUSES.includes(subscription?.status);
		const isProgressLogProgram = PROGRESS_LOG_PROGRAM_SLUGS.has(enrolledProgram?.slug);
		const isProgressLogProduct = PROGRESS_LOG_PRODUCT_SKUS.has(subscription?.productSku);

		return {
			id: subscription?.id ?? null,
			status: subscription?.status ?? null,
			programId: subscription?.programId ?? null,
			programSlug: enrolledProgram?.slug ?? null,
			productSku: subscription?.productSku ?? null,
			isActiveStatus,
			isProgressLogProgram,
			isProgressLogProduct,
			grantsAccess: isActiveStatus && (isProgressLogProgram || isProgressLogProduct),
		};
	});
	const hasProgressLogSubscription = subscriptionSummaries.some((subscription) => subscription.grantsAccess);
	const isRep = hasRepPermissions(authContext);

	return {
		canAccess: hasProgressLogSubscription || isRep,
		hasProgressLogSubscription,
		isRep,
		subscriptionSummaries,
	};
};

/**
 * Convenience wrapper over the progress log access decision.
 *
 * @param {object} [options]
 * @param {object} [options.authContext={}] - Current authorization context used for permission checks.
 * @param {Array<object>} [options.subscriptions=[]] - User subscriptions used to detect progress log program enrollment.
 * @param {Array<object>} [options.programs=[]] - Program records used to resolve a subscription's program slug.
 * @returns {boolean} Whether the user may reach the progress log.
 */
export const canAccessProgressLog = ({ authContext = {}, subscriptions = [], programs = [] } = {}) => {
	return getProgressLogAccessDecision({ authContext, subscriptions, programs }).canAccess;
};
